/**
 * Lightweight Syntax Highlighter
 * Keyword-based highlighting for common languages — no external deps.
 */

const LANGS = {
  js: {
    keywords: /\b(const|let|var|function|return|if|else|for|while|do|switch|case|break|continue|class|extends|new|this|super|import|export|default|from|async|await|try|catch|finally|throw|typeof|instanceof|in|of|void|delete|yield|static|get|set)\b/g,
    builtins: /\b(console|window|document|Math|JSON|Promise|Array|Object|String|Number|Boolean|Map|Set|Date|RegExp|Error|undefined|null|true|false|NaN|Infinity)\b/g,
    strings: /(["'`])(?:(?!\1|\\).|\\.)*?\1/g,
    comments: /\/\/.*$|\/\*[\s\S]*?\*\//gm,
    numbers: /\b(\d+\.?\d*(?:e[+-]?\d+)?)\b/gi,
    functions: /\b([a-zA-Z_$][\w$]*)\s*(?=\()/g,
  },
  typescript: null, // alias to js
  ts: null,
  jsx: null,
  tsx: null,
  python: {
    keywords: /\b(def|class|if|elif|else|for|while|return|import|from|as|try|except|finally|raise|with|yield|lambda|pass|break|continue|and|or|not|is|in|global|nonlocal|assert|del|async|await)\b/g,
    builtins: /\b(print|len|range|int|str|float|list|dict|set|tuple|bool|None|True|False|type|isinstance|enumerate|zip|map|filter|sorted|super|self|__init__|__name__|__main__)\b/g,
    strings: /("""[\s\S]*?"""|'''[\s\S]*?'''|"(?:[^"\\]|\\.)*"|'(?:[^'\\]|\\.)*')/g,
    comments: /#.*$/gm,
    numbers: /\b(\d+\.?\d*(?:e[+-]?\d+)?j?)\b/gi,
    functions: /\b([a-zA-Z_]\w*)\s*(?=\()/g,
    decorators: /@[\w.]+/g,
  },
  py: null,
  html: {
    tags: /(&lt;\/?)([\w-]+)/g,
    attrs: /\b([\w-]+)(?==)/g,
    strings: /(["'])(?:(?!\1).)*?\1/g,
    comments: /&lt;!--[\s\S]*?--&gt;/g,
  },
  xml: null,
  css: {
    selectors: /^([.#]?[\w-]+(?:\s*[,>+~]\s*[.#]?[\w-]+)*)\s*(?=\{)/gm,
    properties: /([\w-]+)\s*(?=:)/g,
    values: /:\s*([^;{}]+)/g,
    strings: /(["'])(?:(?!\1).)*?\1/g,
    comments: /\/\*[\s\S]*?\*\//g,
    numbers: /\b(\d+\.?\d*)(px|em|rem|%|vh|vw|s|ms|deg|fr)?\b/g,
  },
  json: {
    keys: /"([^"]+)"\s*(?=:)/g,
    strings: /:\s*("(?:[^"\\]|\\.)*")/g,
    numbers: /:\s*(-?\d+\.?\d*(?:e[+-]?\d+)?)/gi,
    booleans: /\b(true|false|null)\b/g,
  },
  bash: {
    keywords: /\b(if|then|else|elif|fi|for|while|do|done|case|esac|function|return|in|select|until|export|source|alias|unalias|set|unset|readonly|local|declare|typeset|eval|exec|exit|trap)\b/g,
    builtins: /\b(echo|cd|ls|pwd|cat|grep|sed|awk|find|mkdir|rm|cp|mv|chmod|chown|curl|wget|git|npm|node|python|pip|docker|sudo|apt|yum|brew)\b/g,
    strings: /(["'])(?:(?!\1|\\).|\\.)*?\1/g,
    comments: /#.*$/gm,
    variables: /\$\{?\w+\}?/g,
    numbers: /\b(\d+)\b/g,
  },
  sh: null,
  shell: null,
  zsh: null,
  sql: {
    keywords: /\b(SELECT|FROM|WHERE|INSERT|UPDATE|DELETE|CREATE|ALTER|DROP|TABLE|INDEX|VIEW|JOIN|LEFT|RIGHT|INNER|OUTER|ON|AND|OR|NOT|IN|EXISTS|BETWEEN|LIKE|ORDER|BY|GROUP|HAVING|LIMIT|OFFSET|UNION|ALL|AS|DISTINCT|INTO|VALUES|SET|NULL|IS|CASE|WHEN|THEN|ELSE|END|PRIMARY|KEY|FOREIGN|REFERENCES|DEFAULT|CHECK|UNIQUE|CASCADE|CONSTRAINT)\b/gi,
    strings: /(["'])(?:(?!\1).)*?\1/g,
    comments: /--.*$|\/\*[\s\S]*?\*\//gm,
    numbers: /\b(\d+\.?\d*)\b/g,
    functions: /\b(COUNT|SUM|AVG|MIN|MAX|COALESCE|IFNULL|CAST|CONVERT|CONCAT|SUBSTRING|LENGTH|UPPER|LOWER|TRIM|NOW|DATE|YEAR|MONTH|DAY)\b/gi,
  },
};

// Set aliases
LANGS.typescript = LANGS.js;
LANGS.ts = LANGS.js;
LANGS.jsx = LANGS.js;
LANGS.tsx = LANGS.js;
LANGS.py = LANGS.python;
LANGS.xml = LANGS.html;
LANGS.sh = LANGS.bash;
LANGS.shell = LANGS.bash;
LANGS.zsh = LANGS.bash;

/**
 * Apply syntax highlighting to code text (already HTML-escaped)
 * @param {string} code - HTML-escaped code string
 * @param {string} lang - Language identifier
 * @returns {string} HTML with highlight spans
 */
export function highlight(code, lang) {
  if (!lang) return code;
  const rules = LANGS[lang.toLowerCase()];
  if (!rules) return code;

  // Tokenize to avoid double-highlighting
  const tokens = [];
  let tokenId = 0;

  function stash(match, cls) {
    const id = `__TOK${tokenId++}__`;
    tokens.push({ id, html: `<span class="hl-${cls}">${match}</span>` });
    return id;
  }

  let result = code;

  // Order matters: comments and strings first (greedy), then other tokens

  if (rules.comments) {
    result = result.replace(rules.comments, m => stash(m, 'comment'));
  }
  if (rules.decorators) {
    result = result.replace(rules.decorators, m => stash(m, 'decorator'));
  }
  if (rules.strings) {
    result = result.replace(rules.strings, m => stash(m, 'string'));
  }
  if (rules.keys) {
    result = result.replace(rules.keys, (m, key) =>
      m.replace(`"${key}"`, stash(`"${key}"`, 'key'))
    );
  }
  if (rules.tags) {
    result = result.replace(rules.tags, (m, bracket, tag) =>
      bracket + stash(tag, 'tag')
    );
  }
  if (rules.keywords) {
    result = result.replace(rules.keywords, m => stash(m, 'keyword'));
  }
  if (rules.builtins) {
    result = result.replace(rules.builtins, m => stash(m, 'builtin'));
  }
  if (rules.functions) {
    result = result.replace(rules.functions, (m, name) => stash(name, 'function') + m.slice(name.length));
  }
  if (rules.booleans) {
    result = result.replace(rules.booleans, m => stash(m, 'builtin'));
  }
  if (rules.variables) {
    result = result.replace(rules.variables, m => stash(m, 'variable'));
  }
  if (rules.properties) {
    result = result.replace(rules.properties, (m, prop) => stash(prop, 'property'));
  }
  if (rules.numbers) {
    result = result.replace(rules.numbers, (m) => stash(m, 'number'));
  }
  if (rules.selectors) {
    result = result.replace(rules.selectors, m => stash(m, 'selector'));
  }
  if (rules.attrs) {
    result = result.replace(rules.attrs, (m, attr) => stash(attr, 'attr'));
  }

  // Restore tokens
  for (const t of tokens) {
    result = result.replace(t.id, t.html);
  }

  return result;
}
